import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/styles';

const instances = new WeakMap();

function getConfig() {
    return window.DL_PHONE || {};
}

function cleanDigits(value) {
    return String(value || '')
        .replace(/[\/\-().\s]/g, '')
        .replace(/[^\d+]/g, '');
}

function normaliseToE164(input, iti) {
    const selectedCountry = iti?.getSelectedCountryData?.();
    const selectedDialCode = selectedCountry?.dialCode || getConfig().dialCode || '31';
    const rawValue = input.value || '';

    if (!rawValue.trim()) {
        return '';
    }

    const pluginNumber = iti?.getNumber?.();
    if (pluginNumber && /^\+\d{7,15}$/.test(pluginNumber)) {
        return pluginNumber;
    }

    let value = cleanDigits(rawValue);
    if (value.startsWith('00')) {
        value = `+${value.slice(2)}`;
    }

    if (value.startsWith('+')) {
        let digits = value.replace(/\D/g, '');

        if (digits.startsWith(selectedDialCode)) {
            digits = digits.slice(selectedDialCode.length).replace(/^0+/, '');
            return digits ? `+${selectedDialCode}${digits}` : '';
        }

        return digits ? `+${digits}` : '';
    }

    let digits = value.replace(/\D/g, '');

    if (digits.startsWith(selectedDialCode)) {
        digits = digits.slice(selectedDialCode.length);
    }

    digits = digits.replace(/^0+/, '');

    return digits ? `+${selectedDialCode}${digits}` : '';
}

function setInitialNumber(input, iti) {
    const rawValue = input.value || '';
    if (!rawValue.trim()) {
        return;
    }

    const e164 = normaliseToE164(input, iti);
    if (e164) {
        iti.setNumber(e164);
    }
}

function getPlaceholderNumberType(input) {
    return input.name === 'billing_phone_landline' ? 'FIXED_LINE' : 'MOBILE';
}

function getCustomPlaceholder(input) {
    if (input.name !== 'billing_phone_landline') {
        return null;
    }

    return (selectedCountryPlaceholder, selectedCountryData) => {
        if (selectedCountryData?.iso2 === 'be') {
            return '2 123 45 67';
        }

        return '20 12 34 56';
    };
}

async function initialisePhoneInput(input) {
    if (!(input instanceof HTMLInputElement) || instances.has(input)) {
        return;
    }

    const config = getConfig();
    const initialCountry = input.dataset.defaultCountry || config.defaultCountry || 'nl';
    const options = {
        initialCountry,
        countryOrder: config.countryOrder || ['nl', 'be'],
        separateDialCode: true,
        nationalMode: true,
        formatOnDisplay: false,
        placeholderNumberType: getPlaceholderNumberType(input),
        loadUtils: () => import('intl-tel-input/utils'),
    };

    const customPlaceholder = getCustomPlaceholder(input);
    if (customPlaceholder) {
        options.customPlaceholder = customPlaceholder;
    }

    const iti = intlTelInput(input, options);
    instances.set(input, iti);

    if (iti.promise) {
        await iti.promise;
    }

    setInitialNumber(input, iti);

    input.addEventListener('blur', () => {
        const e164 = normaliseToE164(input, iti);
        if (e164) {
            iti.setNumber(e164);
        }
    });
}

function preparePhoneValues(form) {
    getPhoneInputs(form).forEach((input) => {
        const iti = instances.get(input);
        input.value = normaliseToE164(input, iti);
    });
}

function getPhoneInputs(root = document) {
    return root.querySelectorAll(
        'input.js-phone-input, input[data-phone-input="1"], .js-phone-input input[type="tel"]'
    );
}

export async function initPhoneInputs(root = document) {
    const inputs = getPhoneInputs(root);

    for (const input of inputs) {
        await initialisePhoneInput(input);
    }

    root.querySelectorAll('form.checkout, form.woocommerce-form-register, form.woocommerce-EditAccountForm').forEach((form) => {
        if (form.dataset.phoneInputsPrepared) {
            return;
        }

        form.dataset.phoneInputsPrepared = '1';
        form.addEventListener('submit', () => preparePhoneValues(form));
    });
}

document.addEventListener('DOMContentLoaded', () => {
    void initPhoneInputs();

    if (window.jQuery) {
        window.jQuery(document.body).on('updated_checkout', () => {
            void initPhoneInputs();
        });
    }
});
